import { memo, type ReactNode } from 'react';
import {
  SEEDED_PRIORITY_COLORS,
  SEEDED_STATUS_COLORS,
  STATUS_COLORS,
  addWireDays,
  toWireDate,
  type Person,
  type SidebarView,
  type Task,
  type TaskPriority,
  type TaskStatus,
} from '@forecastify/shared';
import { AppShell, PreviewStage, cn } from '@forecastify/ui';
import { useI18n, type Translate } from '../../i18n';
import { avatarSeed } from '../identity';
import { PlainTitle } from '../page-header';
import { PeopleScreen } from '../people/people-screen';
import { sidebarRows } from '../sidebar/sidebar-rows';
import { TasksScreen, type TaskFilters } from '../tasks/tasks-screen';
import { addressFromName } from './setup-rules';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The setup flow's picture of the product — the real shell, out of focus.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE SIDEBAR IS THE APP'S OWN, FROM THE SAME BUILDER `Chrome` USES** (`sidebar-rows.ts`).
 * The mockup drew a seven-row menu of its own; the product owner: *"the sidebar menu renders
 * differently than what we have in the app right now … make sure this preview's sidebar matches
 * what's exactly in the app."* So the rows are whatever `routes.tsx` gives a new workspace's owner:
 * every entitlement, every feature on (unanswered is on, as in `Chrome`), and empty pinned lists.
 *
 * ⚠️⚠️ **AND SINCE 14 OCT THE PANE IS THE APP'S OWN TOO — `TasksScreen` AND `PeopleScreen`.** The
 * mockup's illustration — status rings, *Nyitott / Ez a hét / Kész* tabs and a team panel on the
 * right — drew things the product does not have, and the product owner asked for *"the sections
 * found exactly in the app"*. So steps 1, 2 and 4 show the Feladatok list with its real toolbar and
 * columns, and step 3 cross-fades to Dolgozók, where every address typed becomes a pending
 * invitation row — the row that list draws for one (`person.kind === 'invitation'`). The sidebar's
 * active row follows.
 *
 * ⚠️ **The rows are samples, not data**: the workspace is empty, and a picture of an empty screen
 * shows nothing. The statuses and priorities are the five and four `seed_task_vocabularies` gives a
 * new workspace (`0209`), named through the catalogue so the English picture is English.
 *
 * ⚠️⚠️ **INERT, THREE TIMES OVER.** `PreviewStage` takes the whole picture out of the tab order,
 * the pointer and the accessibility tree; every callback handed to the two screens does nothing;
 * and both are `embedded`, so neither claims the shell's header nor the route gate's `data-screen`
 * marker. Nothing in them fetches: the only request either could make is a profile picture by
 * path, and every sample row's path is `null`.
 */

const NEW_WORKSPACE: SidebarView = {
  counts: {
    projects: 0,
    clients: 0,
    people: 1,
    tasks: 0,
    contracts: 0,
    invoices: 0,
    reports: 0,
    worklogs: 0,
    myOpenTasks: 0,
    pendingApprovals: 0,
    requests: 0,
    deals: 0,
  },
  projects: [],
  reports: [],
  deals: [],
  features: {},
  entitlements: {
    logsTime: true,
    leaveRequest: true,
    leaveDecide: true,
    leaveViewAll: true,
    menuProjects: true,
    menuTasks: true,
    menuContracts: true,
    menuInvoices: true,
    menuWorklogs: true,
    menuPeople: true,
    menuClients: true,
    reports: true,
    dashboardResource: true,
    dashboardLogging: true,
    dashboardUtilization: true,
    dashboardLeave: true,
  },
  /* No demo banner in the picture: it is the shell's, above the sidebar, not part of it. */
  demoData: false,
};

const TASK_TITLES = [
  'setup.preview.task1',
  'setup.preview.task2',
  'setup.preview.task3',
  'setup.preview.task4',
  'setup.preview.task5',
  'setup.preview.task6',
  'setup.preview.task7',
  'setup.preview.task8',
  'setup.preview.task9',
] as const;

/** Per sample row: status, priority (indices into the seeded lists), project, mine, due in days. */
const SAMPLES: readonly (readonly [number, number, number, boolean, number])[] = [
  [2, 1, 0, true, 1],
  [1, 2, 1, true, 2],
  [3, 0, 2, false, 3],
  [1, 2, 0, true, 4],
  [0, 3, 1, false, 7],
  [2, 2, 2, true, 8],
  [1, 1, 1, false, 9],
  [0, 3, 0, false, 11],
  [4, 2, 2, true, 15],
];

/** `0209`'s five, in its order and colours. */
function seededStatuses(t: Translate): TaskStatus[] {
  const status = (
    index: number,
    name: string,
    color: string,
    category: TaskStatus['category'],
  ): TaskStatus => ({
    id: `preview-status-${String(index)}`,
    identifier: `task_status_0000000${String(index)}`,
    name,
    color,
    category,
    position: (index + 1) * 10,
    isDefault: index === 0,
    taskCount: 0,
    version: 1,
  });
  return [
    status(0, t('setup.preview.statusBacklog'), SEEDED_STATUS_COLORS.backlog, 'open'),
    status(1, t('setup.preview.statusNext'), SEEDED_STATUS_COLORS.next, 'open'),
    status(2, t('setup.preview.statusDoing'), SEEDED_STATUS_COLORS.doing, 'in_progress'),
    status(3, t('setup.preview.statusReview'), SEEDED_STATUS_COLORS.review, 'in_review'),
    status(4, t('setup.preview.statusDone'), SEEDED_STATUS_COLORS.done, 'closed'),
  ];
}

/** `0209`'s four. */
function seededPriorities(t: Translate): TaskPriority[] {
  const priority = (index: number, name: string, color: string): TaskPriority => ({
    id: `preview-priority-${String(index)}`,
    identifier: `task_priority_0000000${String(index)}`,
    name,
    color,
    position: (index + 1) * 10,
    isDefault: index === 2,
    taskCount: 0,
    version: 1,
  });
  return [
    priority(0, t('setup.preview.priorityUrgent'), SEEDED_PRIORITY_COLORS.urgent),
    priority(1, t('setup.preview.priorityHigh'), SEEDED_PRIORITY_COLORS.high),
    priority(2, t('setup.preview.priorityMedium'), SEEDED_PRIORITY_COLORS.medium),
    priority(3, t('setup.preview.priorityLow'), SEEDED_PRIORITY_COLORS.low),
  ];
}

const NO_FILTERS: TaskFilters = {
  search: '',
  projectId: '',
  statusId: '',
  priorityId: '',
  assigneeMemberId: '',
  teamId: '',
  dueRange: '',
  estimateRange: '',
  metafields: {},
  component: '',
  fixVersion: '',
  epic: '',
};

const nothing = (): void => undefined;

/** A page of one — the list's real `[<] [1] [>] [total]` pager, which does nothing here. */
const onePage = (total: number) => ({
  hasPrevious: false,
  hasNext: false,
  onPrevious: nothing,
  onNext: nothing,
  index: 0,
  count: 1,
  total,
  onGoTo: nothing,
});

export const PREVIEW_FOCUS = {
  profile: '[data-shell-part="user"]',
  workspace: '[data-shell-part="workspace"]',
  /* Step 3: the people list as a whole, which is where the typed addresses land. */
  people: '[data-preview-part="people"]',
} as const;

export interface SetupPreviewProps {
  /** `people` also switches the pane from Feladatok to Dolgozók. */
  readonly focus: keyof typeof PREVIEW_FOCUS | null;
  readonly sharp: boolean;
  /** Already in the reader's order; empty for "not typed yet". */
  readonly name: string;
  readonly initials: string;
  readonly email: string;
  readonly avatarSrc: string | null;
  readonly workspaceName: string;
  readonly logoSrc: string | null;
  /** Addresses that will be invited, in order. */
  readonly invitees: readonly string[];
}

export function SetupPreview({
  focus,
  sharp,
  name,
  initials,
  email,
  avatarSrc,
  workspaceName,
  logoSrc,
  invitees,
}: SetupPreviewProps): ReactNode {
  const { t } = useI18n();
  const onPeople = focus === 'people';
  /* ⚠️ Relative addresses, so `active` can light the row the pane is showing; `PreviewStage`
     makes every one of these links unreachable. */
  const rows = sidebarRows({
    t,
    view: NEW_WORKSPACE,
    pathname: onPeople ? '/people' : '/tasks',
    here: (path) => path,
    go: () => nothing,
  });
  const shownWorkspace = workspaceName.trim() === '' ? t('setup.preview.workspace') : workspaceName;
  const shownName = name === '' ? t('setup.preview.yourName') : name;
  const seed = email === '' ? null : avatarSeed(email);
  const prefix = (
    addressFromName(workspaceName)
      .split('-')
      .map((word) => word[0] ?? '')
      .join('')
      .slice(0, 3) || 'ws'
  ).toUpperCase();

  /* The pane's two layers share one fade, so the header, the pane and the lifted copy move together. */
  const fade = 'transition-opacity duration-500 motion-reduce:transition-none';

  return (
    <PreviewStage sharp={sharp} focus={focus === null ? null : PREVIEW_FOCUS[focus]}>
      <AppShell
        contained
        /* ⚠️ The search box and the bell too: both are in the real sidebar, and leaving them out
           would be the "differently than the app" the product owner pointed at. */
        labels={{
          navigation: t('nav.main'),
          search: t('nav.search'),
          searchHint: '⌘K',
          notifications: t('nav.notifications'),
          unreadCount: (count) => t('nav.unread', { count }),
          markAllRead: t('nav.markAllRead'),
          noNotifications: t('nav.noNotifications'),
          userMenu: t('nav.userMenu'),
          workspaceMenu: t('nav.workspaceMenu'),
          skipToContent: t('nav.skipToContent'),
        }}
        notifications={[]}
        onOpenSearch={nothing}
        workspace={{
          name: shownWorkspace,
          initial: (workspaceName.trim()[0] ?? 'W').toUpperCase(),
          ...(logoSrc === null ? {} : { iconSrc: logoSrc }),
          menu: [],
        }}
        user={{
          name: shownName,
          email,
          initials: initials === '' ? '·' : initials,
          /* The face the real chip draws for somebody with no picture — `app.tsx` passes the same. */
          avatarSeed: avatarSeed(email),
          ...(avatarSrc === null ? {} : { avatarSrc }),
          menu: [],
        }}
        nav={rows.nav}
        sections={rows.sections}
        header={
          /* The two titles the two screens would claim, one over the other. */
          <span className="grid min-w-0">
            <span className={cn('[grid-area:1/1]', fade, onPeople ? 'opacity-0' : 'opacity-100')}>
              <PlainTitle>{t('title.tasks')}</PlainTitle>
            </span>
            <span className={cn('[grid-area:1/1]', fade, onPeople ? 'opacity-100' : 'opacity-0')}>
              <PlainTitle>{t('title.people')}</PlainTitle>
            </span>
          </span>
        }
      >
        {/* ⚠️ Both screens stay mounted and the people list fades over the task list — a
            cross-fade both ways, with no remount and nothing to load in between. */}
        <div className="relative min-h-0 flex-1">
          <div data-preview-pane="tasks" className="absolute inset-0 flex flex-col">
            <TaskPane prefix={prefix} name={shownName} seed={seed} />
          </div>
          <div
            data-preview-pane="people"
            className={cn(
              'bg-background absolute inset-0 flex flex-col',
              fade,
              onPeople ? 'opacity-100' : 'opacity-0',
            )}
          >
            <div data-preview-part="people" className="bg-background flex min-h-0 flex-1 flex-col">
              <PeoplePane
                name={shownName}
                email={email}
                seed={seed}
                invitees={invitees.join(' ')}
              />
            </div>
          </div>
        </div>
      </AppShell>
    </PreviewStage>
  );
}

/**
 * ⚠️⚠️ **EACH PANE IS MEMOISED ON STRINGS, BECAUSE THE FORM BESIDE IT RE-RENDERS ON EVERY
 * KEYSTROKE.** Two real lists are heavy to redraw, and typing a name in step 2 changes neither of
 * them unless it changes the key prefix — so each pane takes only the primitives it draws from and
 * is skipped otherwise. The invitees arrive space-joined for the same reason: the caller builds a
 * new array every render, a string compares by value, and an address holds no space.
 */
const TaskPane = memo(function TaskPane({
  prefix,
  name,
  seed,
}: {
  readonly prefix: string;
  readonly name: string;
  readonly seed: string | null;
}): ReactNode {
  const { t } = useI18n();
  const statuses = seededStatuses(t);
  const priorities = seededPriorities(t);
  const projects = [
    /* Sample projects, painted from the palette a status or priority is picked from. */
    { id: 'preview-project-0', name: t('setup.preview.project1'), color: STATUS_COLORS[1] },
    { id: 'preview-project-1', name: t('setup.preview.project2'), color: STATUS_COLORS[0] },
    { id: 'preview-project-2', name: t('setup.preview.project3'), color: STATUS_COLORS[4] },
  ];
  const today = toWireDate(new Date());
  const tasks: Task[] = SAMPLES.map(([s, p, j, mine, due], index) => {
    const status = statuses[s] as TaskStatus;
    const priority = priorities[p] as TaskPriority;
    const project = projects[j] as (typeof projects)[number];
    return {
      id: `preview-task-${String(index)}`,
      key: `${prefix}-${String(101 + index)}`,
      keyPending: false,
      title: t(TASK_TITLES[index] ?? 'setup.preview.task1'),
      description: null,
      projectId: project.id,
      projectName: project.name,
      projectColor: project.color,
      statusId: status.id,
      statusName: status.name,
      statusColor: status.color,
      statusCategory: status.category,
      priorityId: priority.id,
      priorityName: priority.name,
      priorityColor: priority.color,
      assigneeMemberId: mine ? 'preview-me' : null,
      assigneeName: mine ? name : null,
      /* ⚠️ Never a path: `useAvatars` would fetch one. The face comes from the seed. */
      assigneeAvatarUrl: null,
      assigneeAvatarSeed: mine ? seed : null,
      lineItemId: null,
      lineItemName: null,
      contractCode: null,
      recurringContractId: null,
      lineItemSource: null,
      estimateHours: String(2 + ((index * 3) % 7)),
      startsOn: null,
      dueOn: addWireDays(today, due),
      parentTaskId: null,
      parentKey: null,
      tags: [],
      tracker: null,
      metafields: {},
      subtaskCount: 0,
      subtaskDoneCount: 0,
      trackedHours: '0',
      version: 1,
    };
  });

  return (
    <TasksScreen
      embedded
      tasks={tasks}
      statuses={statuses}
      priorities={priorities}
      projects={projects}
      members={[{ id: 'preview-me', name }]}
      filters={NO_FILTERS}
      onFiltersChange={nothing}
      canCreate
      canEdit
      onCreate={nothing}
      onOpen={nothing}
      onMoveStatus={nothing}
      onReschedule={nothing}
      onGridEdit={nothing}
      onExport={nothing}
      onImport={nothing}
      page={onePage(tasks.length)}
      serverError={null}
    />
  );
});

const PeoplePane = memo(function PeoplePane({
  name,
  email,
  seed,
  invitees,
}: {
  readonly name: string;
  readonly email: string;
  readonly seed: string | null;
  /** Space-joined — see above. */
  readonly invitees: string;
}): ReactNode {
  const { t } = useI18n();
  /* The owner, as the list draws a member — and each typed address as the row it will be. */
  const people: Person[] = [
    samplePerson({
      id: 'preview-me',
      identifier: 'member_00000000',
      displayName: name,
      email,
      avatarSeed: seed,
      status: 'active',
      tracksTime: true,
      roles: [{ id: 'preview-owner', name: t('setup.preview.ownerRole'), code: 'OWNER' }],
      kind: 'member',
    }),
    ...(invitees === '' ? [] : invitees.split(' ')).map((address, index) =>
      samplePerson({
        id: `preview-invite-${String(index)}`,
        identifier: 'invitation_00000000',
        displayName: address,
        email: address,
        avatarSeed: null,
        status: 'invited',
        tracksTime: false,
        roles: [],
        kind: 'invitation',
      }),
    ),
  ];

  return (
    <PeopleScreen
      embedded
      people={people}
      teams={[]}
      search=""
      team=""
      status=""
      employmentType=""
      tracks=""
      includeDeleted={false}
      canDeactivate
      canOnboard
      serverError={null}
      onSearch={nothing}
      onTeam={nothing}
      onStatus={nothing}
      onEmploymentType={nothing}
      onTracks={nothing}
      onIncludeDeleted={nothing}
      sort={[]}
      onSortChange={nothing}
      onOpen={nothing}
      onSetStatus={nothing}
      onDelete={nothing}
      onOnboard={nothing}
      onInvite={nothing}
      page={onePage(people.length)}
    />
  );
});

/** A `Person` with the constants the people service gives a new row — `person.service.ts`. */
function samplePerson(
  fields: Pick<
    Person,
    | 'id'
    | 'identifier'
    | 'displayName'
    | 'email'
    | 'avatarSeed'
    | 'status'
    | 'tracksTime'
    | 'roles'
    | 'kind'
  >,
): Person {
  return {
    ...fields,
    avatarUrl: null,
    givenName: null,
    familyName: null,
    phone: null,
    employmentType: 'internal',
    seniority: null,
    teams: [],
    location: null,
    timezone: 'Europe/Budapest',
    language: 'hu',
    leaveManaged: true,
    resourcePlanned: true,
    managerMemberId: null,
    managerName: null,
    startsOn: null,
    endsOn: null,
    onboardedAt: null,
    deletedAt: null,
    monthHours: '0',
    monthSplit: {},
    defaultProjectRole: null,
    projectCount: 0,
    metafields: {},
    version: 1,
  };
}
